import {
  clientApplicationConstructionType,
  clientApplicationProjectStatus,
  clientApplicationProjectType,
} from '@/components/Applications/ClientApplication/constants';
import type { JsonInput } from '@/components/UI/JsonForm/JsonInputRenderer/types';
import type { IApplicationFormStep } from '@/features/ApplicationForm/types';
import { formatDate3 } from '@/utils';
import { States } from '../../../../constants';

const abbInformation: JsonInput[] = [
  {
    type: 'input',
    props: {
      name: 'organizationInformation.businessName',
      label: 'Business Name',
    },
  },
  {
    type: 'input',
    props: {
      name: 'organizationInformation.businessAddress',
      label: 'Business Address',
    },
  },
  {
    type: 'input',
    props: {
      name: 'organizationInformation.businessCity',
      label: 'Business City',
    },
  },
  {
    type: 'select',
    props: {
      name: 'organizationInformation.state',
      label: 'State',
      options: States.map((state) => ({
        label: state.name,
        value: state.abbreviation,
      })),
    },
  },
  {
    type: 'masked',
    props: {
      name: 'organizationInformation.zipCode',
      label: 'Zip / Postal Code',
      maskedComponent: 'zipCode',
    },
  },
  {
    type: 'masked',
    props: {
      name: 'organizationInformation.businessPhone',
      label: 'Business Phone',
      maskedComponent: 'phone',
    },
  },
  {
    type: 'input',
    props: {
      name: 'organizationInformation.website',
      label: 'URL / Web Address (Optional)',
    },
  },
  {
    type: 'date',
    props: {
      name: 'organizationInformation.establishedDate',
      label: 'Date established / Date of Incorporation',
      max: formatDate3(new Date().toISOString(), 'yyyy-mm-dd'),
    },
  },
  {
    type: 'masked',
    props: {
      name: 'organizationInformation.fein',
      label: 'FEIN #',
      maskedComponent: 'ein',
    },
  },
  {
    type: 'input',
    props: {
      name: 'organizationInformation.leadContact',
      label: 'Lead Contact',
    },
  },
  {
    type: 'masked',
    props: {
      name: 'organizationInformation.phone',
      label: 'Phone',
      maskedComponent: 'phone',
    },
  },
  {
    type: 'input',
    props: {
      name: 'organizationInformation.email',
      label: 'E-mail',
    },
  },
  {
    type: 'file',
    gridColSpan: 'col-span-12',
    props: {
      name: 'organizationInformation.businessTaxReturns',
      label: 'Please upload the past three years of tax returns and the most recent financial statements',
      multiple: true,
    },
  },
];
const cclfFinancing: JsonInput[] = [
  {
    type: 'checkbox',
    gridColSpan: 'col-span-12 relative mt-5',
    props: {
      name: 'organizationInformation.cclfFinancing',
      className: 'absolute bottom-0',
      label: 'Check here if you are interested in CCLF financing for your project.',
    },
  },
];
const socialDesignation: JsonInput[] = [
  {
    type: 'radio-group',
    gridColSpan: 'col-span-12',
    props: {
      name: 'organizationInformation.socialDesignation',
      hasOthers: true,
      options: [
        { label: 'Minority Owned', value: 'Minority Owned' },
        { label: 'Woman Owned', value: 'Woman Owned' },
        {
          label: 'Disadvantaged Business Enterprise',
          value: 'Disadvantaged Business Enterprise',
        },
        { label: 'Veteran Owned', value: 'Veteran Owned' },
        { label: 'Local Business', value: 'Local Business' },
      ],
    },
  },
];
const legalStatus: JsonInput[] = [
  {
    type: 'radio-group',
    gridColSpan: 'col-span-12 -mt-5',
    props: {
      name: 'organizationInformation.legalStatus',
      hasOthers: true,
      options: [
        { label: 'LLC', value: 'llc' },
        { label: 'LLP', value: 'llp' },
        { label: 'LLLP', value: 'lllp' },
        { label: 'Corporation', value: 'corporation' },
        { label: 'Tax Exempt 501 (c)3', value: 'tax-exempt-501-c-3' },
      ],
    },
  },
];
const gcsInformation: JsonInput[] = [
  {
    type: 'empty',
    props: {} as JsonInput['props'],
    isArray: true,
    minArrayLength: 1,
    gridColSpan: 'col-span-12',
    arrayName: 'organizationInformation.sponsors',
    inputs: [
      {
        type: 'input',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'name',
          label: 'Name',
        },
      },
      {
        type: 'input',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'address',
          label: 'Address',
        },
      },
      {
        type: 'input',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'city',
          label: 'City',
        },
      },
      {
        type: 'select',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'state',
          label: 'State',
          options: States.map((state) => ({
            label: state.name,
            value: state.abbreviation,
          })),
        },
      },
      {
        type: 'masked',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'zipCode',
          label: 'Zip',
          maskedComponent: 'zipCode',
        },
      },
      {
        type: 'masked',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'phone',
          label: 'Phone',
          maskedComponent: 'phone',
        },
      },
      {
        type: 'input',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'email',
          label: 'E-mail',
        },
      },
      {
        type: 'masked',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'socialSecurity',
          label: 'SS #',
          maskedComponent: 'socialSecurity',
        },
      },
      {
        type: 'input',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'ownershipPercent',
          label: 'Ownership %',
          type: 'number',
        },
      },
      //  {
      //    type: "file",
      //  gridColSpan: "col-span-12",
      //  props: {
      //    name: "letterOfExplanation",
      //    label: "Letter of Explanation",
      //    multiple: true,
      //  },
      //},
      {
        type: 'file',
        gridColSpan: 'col-span-6',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'personalFinancialStatements',
          label: 'Please upload your Personal Financial Statement (PFS)',
          multiple: true,
        },
      },
      {
        type: 'file',
        gridColSpan: 'col-span-6',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'personalTaxReturns',
          label: 'Please upload the past three years of tax returns',
          multiple: true,
        },
      },

      {
        type: 'info',
        gridColSpan: 'col-span-12',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: false,
        props: {
          name: 'info',
          label: `<a href="https://sageterminal.com/data/CCLF_PFS.pdf" target="_blank" rel="no-referrer" class="underline">Download PFS template</a>`,
        },
      },
      {
        type: 'checkbox',
        gridColSpan: 'col-span-12 relative mt-6',
        props: {
          name: 'isCorporate',
          className: 'absolute bottom-0',
          label: 'Check here if the project guarantor is a corporation',
        },
      },
      {
        type: 'date',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: true,
        props: {
          name: 'establishedDate',
          label: 'Date established / Date of incorporation',
          max: formatDate3(new Date().toISOString(), 'yyyy-mm-dd'),
        },
      },
      {
        type: 'masked',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: true,
        props: {
          name: 'fein',
          label: 'FEIN #',
          maskedComponent: 'ein',
        },
      },
      {
        type: 'input',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: true,
        props: {
          name: 'website',
          label: 'URL / Web Address (Optional)',
        },
      },
      {
        type: 'input',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: true,
        props: {
          name: 'leadContact',
          label: 'Lead Contact',
        },
      },
      {
        type: 'masked',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: true,
        props: {
          name: 'contactPhone',
          label: 'Contact Phone',
          maskedComponent: 'phone',
        },
      },
      {
        type: 'input',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: true,
        props: {
          name: 'contactEmail',
          label: 'Contact E-mail',
        },
      },
      {
        type: 'file',
        gridColSpan: 'col-span-12',
        isDependent: true,
        dependentInputName: 'isCorporate',
        dependentInputValue: true,
        props: {
          name: 'corporateTaxReturns',
          label: 'Please upload 3 years of Corporate Tax Returns',
          multiple: true,
        },
      },
      // {
      //   type: "info",
      //   gridColSpan: "col-span-12",
      //   props: {
      //     name: "consideration",
      //     label:
      //       "If you are <strong>not interested in</strong> receiving further consideration for <strong>credit</strong> with your RFQ for the CNRP, please stop once complete with page 1, <strong>check</strong> the box, and sign below: ",
      //   },
      // },
      // {
      //   type: "checkbox",
      //   gridColSpan: "col-span-12 relative",
      //   props: {
      //     name: "optCredit",
      //     className: "absolute bottom-0",
      //     label: "Opt Credit",
      //   },
      // },
      // {
      //   type: "input",
      //   isDependent: true,
      //   dependentInputName: "optCredit",
      //   gridColSpan: "col-span-6",
      //   dependentInputValue: true,
      //   props: {
      //     name: "optName",
      //     label: "Name",
      //   },
      // },
      // {
      //   type: "date",
      //   isDependent: true,
      //   dependentInputName: "optCredit",
      //   gridColSpan: "col-span-6",
      //   dependentInputValue: true,
      //   props: {
      //     name: "optDate",
      //     label: "Date",
      //     min: formatDate3(new Date().toISOString(), "yyyy-mm-dd"),
      //   },
      // },
    ],
  },
];

const priorProject: JsonInput[] = [
  {
    type: 'empty',
    props: {} as JsonInput['props'],
    isArray: true,
    minArrayLength: 1,
    gridColSpan: 'col-span-12',
    arrayName: 'organizationExperience.priorProjects',
    inputs: [
      {
        type: 'input',
        props: {
          name: 'address',
          label: 'Address',
        },
      },
      {
        type: 'input',
        props: {
          name: 'city',
          label: 'City',
        },
      },
      {
        type: 'select',
        props: {
          name: 'state',
          label: 'State',
          options: States.map((state) => ({
            label: state.name,
            value: state.abbreviation,
          })),
        },
      },
      {
        type: 'masked',
        props: {
          name: 'zipCode',
          label: 'Zip / Postal Code',
          maskedComponent: 'zipCode',
        },
      },
      {
        type: 'select',
        props: {
          name: 'projectType',
          label: 'Project Type',
          options: Object.keys(clientApplicationProjectType).map((key) => ({
            label: clientApplicationProjectType[key],
            value: key,
          })),
        },
      },
      {
        type: 'select',
        props: {
          name: 'constructionType',
          label: 'Construction Type',
          options: Object.keys(clientApplicationConstructionType).map((key) => ({
            label: clientApplicationConstructionType[key],
            value: key,
          })),
        },
      },
      {
        type: 'date',
        props: {
          name: 'completionDate',
          label: 'Completion Date',
          max: formatDate3(new Date().toISOString(), 'yyyy-mm-dd'),
        },
      },
      {
        type: 'select',
        props: {
          name: 'status',
          label: 'Status',
          options: Object.keys(clientApplicationProjectStatus).map((key) => ({
            label: clientApplicationProjectStatus[key],
            value: key,
          })),
        },
      },
      {
        type: 'masked',
        props: {
          name: 'projectCost',
          label: 'Total Project Cost',
          maskedComponent: 'currency',
        },
      },
      {
        type: 'file',
        gridColSpan: 'col-span-12',
        props: {
          name: 'pictures',
          label: 'Pictures',
          multiple: true,
        },
      },
    ],
  },
];

const keyPrincipals: JsonInput[] = [
  {
    type: 'empty',
    props: {} as JsonInput['props'],
    isArray: true,
    minArrayLength: 1,
    gridColSpan: 'col-span-12',
    arrayName: 'organizationExperience.keyPrincipals',
    inputs: [
      {
        type: 'input',
        props: {
          name: 'name',
          label: 'Name',
        },
      },
      {
        type: 'input',
        props: {
          name: 'ownershipPercent',
          label: 'Ownership %',
          type: 'number',
        },
      },
      {
        type: 'input',
        props: {
          name: 'yearsExperience',
          label: 'Years of Experience',
          type: 'number',
        },
      },
      {
        type: 'file',
        gridColSpan: 'col-span-12',
        props: {
          name: 'principalResumes',
          label: 'Principal Resumes',
          multiple: true,
        },
      },
    ],
  },
];

const sourcesUses: JsonInput[] = [
  {
    type: 'info-with-inputs',
    gridColSpan: 'col-span-6 space-y-5',
    props: {
      name: 'sources',
      label: 'Sources',
      customClassName: 'font-bold',
    },
    inputs: [
      {
        type: 'masked',
        props: {
          name: 'financialRequest.projectInfo.sources.borrowerEquity',
          label: 'Borrower Equity',
          maskedComponent: 'currency',
        },
      },
      {
        type: 'masked',
        props: {
          name: 'financialRequest.projectInfo.sources.debt',
          label: 'Debt',
          maskedComponent: 'currency',
        },
      },
      // {
      //   type: "masked",
      //   props: {
      //     name: "totalSources",
      //     label: "Total Sources",
      //     maskedComponent: "numeric",
      //   },
      // },
    ],
  },
  {
    type: 'info-with-inputs',
    gridColSpan: 'col-span-6 space-y-5',
    props: {
      name: 'uses',
      label: 'Uses (can use historical performance as a basis for an estimate.)',
      customClassName: 'font-bold',
    },
    inputs: [
      {
        type: 'masked',
        props: {
          name: 'financialRequest.projectInfo.uses.averageAcquisition',
          label: 'Acquisition',
          maskedComponent: 'currency',
        },
      },
      {
        type: 'masked',
        props: {
          name: 'financialRequest.projectInfo.uses.averageRehabilitation',
          label: 'Rehabilitation',
          maskedComponent: 'currency',
        },
      },
      {
        type: 'masked',
        props: {
          name: 'financialRequest.projectInfo.uses.tenPContingency',
          label: '10 % Contigency',
          maskedComponent: 'currency',
        },
      },
      {
        type: 'masked',
        props: {
          name: 'financialRequest.projectInfo.uses.softCost',
          label: 'Soft Costs',
          maskedComponent: 'currency',
        },
      },
      {
        type: 'masked',
        props: {
          name: 'financialRequest.projectInfo.uses.closingCost',
          label: 'Closing Costs',
          maskedComponent: 'currency',
        },
      },
    ],
  },
];

const projectInfo: JsonInput[] = [
  {
    type: 'input',
    gridColSpan: 'col-span-12',
    props: {
      name: 'financialRequest.projectInfo.address',
      label: 'Address',
    },
  },
  {
    type: 'input',
    props: {
      name: 'financialRequest.projectInfo.city',
      label: 'City',
    },
  },
  {
    type: 'select',
    props: {
      name: 'financialRequest.projectInfo.state',
      label: 'State',
      options: States.map((state) => ({
        label: state.name,
        value: state.abbreviation,
      })),
    },
  },
  {
    type: 'masked',
    props: {
      name: 'financialRequest.projectInfo.zipCode',
      label: 'Zip / Postal Code',
      maskedComponent: 'zipCode',
    },
  },
  ...sourcesUses,
];

const loanRequest: JsonInput[] = [
  {
    type: 'masked',
    gridColSpan: 'col-span-6',
    props: {
      name: 'financialRequest.loanRequest.loanRequestAmount',
      label: 'Loan Request Amount',
      maskedComponent: 'currency',
    },
  },
  {
    type: 'masked',
    gridColSpan: 'col-span-6',
    props: {
      name: 'financialRequest.loanRequest.maxUnits',
      label: 'Max # of units at any given time',
      maskedComponent: 'numeric',
    },
  },
];

const disclosures: JsonInput[] = [
  {
    type: 'info',
    gridColSpan: 'col-span-12',
    props: {
      name: 'info',
      label: `<p class="mb-3 leading-loose">The undersigned applicant(s) do hereby represent and warrant that the information contained in this application, and any attachments submitted in conjunction with it, is complete and correct. Furthermore, <strong>applicant(s) authorize CCLF and/or any of its affiliate to obtain credit references, credit reports on the business, credit reports for the personal guarantors, and to release credit information to others. </strong> All applications are subject to final credit approval. CCLF and its affiliates reserve all rights to publicly announce the approval, commitment, or closing of any loan.   
  </p>
  <p class="mb-3 leading-loose">
  The undersigned hereby certifies that, to the best of his/her knowledge, as of the date of this loan application, neither the undersigned, nor any of its officers, directors, trustees or affiliates (collectively, the “Undersigned”), has ever been charged with, indicted or convicted of, or pled guilty to, a felony of any kind, a crime involving fraud or any misdemeanor involving moral turpitude. The undersigned certifies that its internal operations and service are administered on a non-discriminatory basis without regard to race, creed, color, sex, sexual orientation, disability, marital status, veteran status, national origin, age or income level. The undersigned also certifies that the business is not military/defense related in is non-nuclear, and complies with federal, state, and local regulations regarding air, water, toxic materials, hazardous wastes and occupational safety.  
  </p>
  <p class="mb-3 leading-loose">
  The undersigned hereby authorizes CCLF to perform background checks on the Undersigned, as CCLF may determine in its sole discretion.  The undersigned also herby authorizes CCLF to contact the borrower/guarantors for all needed social impact data requested by CCLF’s investors. 
  </p>       
  `,
    },
  },
];

const authorizedSigner: JsonInput[] = [
  {
    type: 'empty',
    isArray: true,
    arrayAddButtonLabel: 'Add Authorized Signer',
    arrayName: 'disclosuresReferences.disclosure.authorizedSigners',
    gridColSpan: 'col-span-12',
    props: {} as JsonInput['props'],
    minArrayLength: 1,
    inputs: [
      {
        type: 'input',
        gridColSpan: 'col-span-12',
        props: {
          name: 'authorizedSigner',
          label: 'Authorized Signer',
        },
      },
      // {
      //   type: "date",
      //   gridColSpan: "col-span-6",
      //   props: {
      //     name: "date",
      //     label: "Date",
      //     min: formatDate3(new Date().toISOString(), "yyyy-mm-dd"),
      //   },
      // },
    ],
  },
];
const lawsuits: JsonInput[] = [
  {
    type: 'checkbox',
    gridColSpan: 'col-span-12 relative mt-10',
    props: {
      name: 'disclosuresReferences.disclosure.hasLawsuits',
      className: 'absolute bottom-0',
      label: 'Check if the applicant is involved or threatened with a bankruptcy, lawsuit or other litigation.',
    },
  },
  {
    type: 'textarea',
    gridColSpan: 'col-span-12',
    isDependent: true,
    dependentInputName: 'disclosuresReferences.disclosure.hasLawsuits',
    dependentInputValue: true,
    props: {
      name: 'disclosuresReferences.disclosure.hasLawsuitsExplain',
      label: 'Briefly describe the nature of the case here:',
    },
  },

  {
    type: 'file',
    gridColSpan: 'col-span-12',
    isDependent: true,
    dependentInputName: 'disclosuresReferences.disclosure.hasLawsuits',
    dependentInputValue: true,
    props: {
      name: 'disclosuresReferences.disclosure.hasLawsuitsSupportingDoc',
      label: 'Supporting Documents',
      multiple: true,
    },
  },

  {
    type: 'checkbox',
    gridColSpan: 'col-span-12 relative mt-6',
    props: {
      name: 'disclosuresReferences.disclosure.defaultedOnLoan',
      className: 'absolute bottom-0',
      label: 'Check if the application has ever defaulted on a loan.',
    },
  },
  {
    type: 'textarea',
    gridColSpan: 'col-span-12',
    isDependent: true,
    dependentInputName: 'disclosuresReferences.disclosure.defaultedOnLoan',
    dependentInputValue: true,
    props: {
      name: 'disclosuresReferences.disclosure.defaultLoanExplain',
      label: 'Please Explain:',
    },
  },

  {
    type: 'file',
    gridColSpan: 'col-span-12',
    isDependent: true,
    dependentInputName: 'disclosuresReferences.disclosure.defaultedOnLoan',
    dependentInputValue: true,
    props: {
      name: 'disclosuresReferences.disclosure.defaultedOnLoanSupportingDoc',
      label: 'Supporting Documents',
      multiple: true,
    },
  },
];
const characterReference: JsonInput[] = [
  {
    type: 'empty',
    isArray: true,
    minArrayLength: 1,
    arrayAddButtonLabel: 'Add Character Reference',
    arrayName: 'disclosuresReferences.characterReference',
    gridColSpan: 'col-span-12',
    props: {} as JsonInput['props'],
    inputs: [
      {
        type: 'input',
        gridColSpan: 'col-span-6',

        props: {
          name: 'name',
          label: 'Name',
        },
      },
      {
        type: 'input',
        gridColSpan: 'col-span-6',

        props: {
          name: 'relationship',
          label: 'Relationship to Applicant',
        },
      },
      {
        type: 'masked',
        gridColSpan: 'col-span-6',

        props: {
          name: 'phone',
          label: 'Phone',
          maskedComponent: 'phone',
        },
      },
      {
        type: 'input',
        gridColSpan: 'col-span-6',
        props: {
          name: 'email',
          label: 'E-mail',
        },
      },
    ],
  },
];

export const chicagoRebuildSteps: IApplicationFormStep[] = [
  {
    title: 'Information',
    sections: [
      {
        id: 'abbInformation',
        title: 'Applicant / Business / Borrower Information',
        inputs: abbInformation,
      },
      {
        id: 'cclfFinancing',
        title: 'CCLF Financing',
        inputs: cclfFinancing,
      },
      {
        id: 'socialDesignation',
        title: 'Social Designation',
        inputs: socialDesignation,
      },
      {
        id: 'legalStatus',
        title: 'Tax / Legal Status',
        inputs: legalStatus,
      },
      {
        id: 'gcsInformation',
        title: 'Guarantor / Corporate Sponsor Information',
        inputs: gcsInformation,
      },
    ],
  },
  {
    title: 'Experience',
    sections: [
      {
        id: 'priorProject',
        title: 'Prior Project',
        inputs: priorProject,
      },
      {
        id: 'keyPrincipals',
        title: 'Key Principals',
        inputs: keyPrincipals,
      },
    ],
  },
  {
    title: 'Loan',
    sections: [
      {
        id: 'loanRequest',
        title: 'Loan Request',
        inputs: loanRequest,
      },
      {
        id: 'projectInfo',
        title: 'Project Information (Optional)',
        inputs: projectInfo,
        name: 'projectInfoSection',
      },
    ],
  },
  {
    title: 'Credit Authorization Request',
    sections: [
      {
        id: 'disclosures',
        title: 'Credit Authorization Request ',
        inputs: disclosures,
      },
      {
        id: 'authorizedSigner',
        inputs: authorizedSigner,
      },
      {
        id: 'lawSuits',
        inputs: lawsuits,
      },
      {
        id: 'characterReference',
        title: 'Character Reference',
        inputs: characterReference,
      },
    ],
  },
  {
    title: 'Review',
    sections: [],
    isReview: true,
  },
];
